import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useMsal } from "@azure/msal-react";
import { EventType } from "@azure/msal-browser";
import { loginRequest, graphConfig } from "../authConfig";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// ✅ Session tracking for THIS app
const AppSession = {
  SESSION_KEY: `${window.location.hostname.replace(/\./g, "_")}_app_session`,

  isLoggedIn: () => {
    try {
      return sessionStorage.getItem(AppSession.SESSION_KEY) === "true";
    } catch (e) {
      return false;
    }
  },

  setLoggedIn: () => {
    try {
      sessionStorage.setItem(AppSession.SESSION_KEY, "true");
    } catch (e) {
      console.warn("Failed to set session:", e);
    }
  },

  clearSession: () => {
    try {
      sessionStorage.removeItem(AppSession.SESSION_KEY);
    } catch (e) {
      console.warn("Failed to clear session:", e);
    }
  },
};

// Enhanced cross-tab communication
const TabSync = {
  STORAGE_KEY: "msal_tab_sync",

  broadcast: (event) => {
    try {
      const data = {
        event,
        timestamp: Date.now(),
        tabId: sessionStorage.getItem("tabId") || Math.random().toString(),
      };
      localStorage.setItem(TabSync.STORAGE_KEY, JSON.stringify(data));
      localStorage.removeItem(TabSync.STORAGE_KEY);
    } catch (e) {
      console.warn("Broadcast failed:", e);
    }
  },

  listen: (callback) => {
    const handler = (e) => {
      if (e.key === TabSync.STORAGE_KEY && e.newValue) {
        try {
          const data = JSON.parse(e.newValue);
          const myTabId = sessionStorage.getItem("tabId");
          if (data.tabId !== myTabId) {
            callback(data.event);
          }
        } catch (err) {
          console.warn("Parse failed:", err);
        }
      }
    };

    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  },
};

export const AuthProvider = ({ children }) => {
  const { instance, accounts } = useMsal();
  const [user, setUser] = useState(null);
  const [backendUser, setBackendUser] = useState(null);
  const [preferences, setPreferences] = useState({ theme: "light" });
  const [isLoading, setIsLoading] = useState(true);
  const avatarUrlRef = useRef(null);
  const lastCheckRef = useRef(Date.now());
  const initRef = useRef(false);
  const loginInProgressRef = useRef(false); // ✅ Track login attempts
  const logoutInProgressRef = useRef(false); // ✅ Track logout attempts

  // Sync user with backend after Azure AD login
  const syncUserWithBackend = useCallback(async (azureUser) => {
    try {
      const userData = {
        user_id: azureUser.id,
        email: azureUser.email,
        display_name: azureUser.name,
        first_name: azureUser.profile?.givenName || null,
        last_name: azureUser.profile?.surname || null,
      };

      // Import api dynamically to avoid circular dependencies
      const api = (await import("../services/api")).default;

      // Use the loginUser endpoint which handles create_or_get
      const backendUserData = await api.loginUser(userData);

      setBackendUser(backendUserData);

      // Load user preferences from backend response
      if (backendUserData.preferences) {
        setPreferences(backendUserData.preferences);
      }

      return backendUserData;
    } catch (error) {
      console.error("Failed to sync user with backend:", error);
      return null;
    }
  }, []);

  // Update user preferences in backend
  const updatePreferences = useCallback(
    async (newPreferences) => {
      // Use the backend user_id which is the Azure AD Object ID
      const userId = backendUser?.user_id || user?.id;
      if (!userId) {
        console.warn("Cannot update preferences: no user ID available");
        return null;
      }

      try {
        const api = (await import("../services/api")).default;
        const updatedPrefs = await api.updateUserPreferences(
          userId,
          newPreferences,
        );
        setPreferences(updatedPrefs);
        console.log("Preferences updated in backend:", updatedPrefs);
        return updatedPrefs;
      } catch (error) {
        console.error("Failed to update preferences:", error);
        return null;
      }
    },
    [backendUser, user],
  );

  // ✅ Safari-specific: Clean up stale interaction flags on mount
  useEffect(() => {
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    if (isSafari) {
      try {
        // Check if login attempt is stale (older than 10 seconds)
        const lastAttempt = sessionStorage.getItem("login_attempt_time");
        if (lastAttempt) {
          const age = Date.now() - parseInt(lastAttempt, 10);
          if (age > 10000) {
            console.log("Safari: Clearing stale login attempt flag");
            sessionStorage.removeItem("login_attempt_time");
            loginInProgressRef.current = false;
          }
        }
      } catch (e) {
        console.warn("Failed to clean stale flags:", e);
      }
    }
  }, []);

  // Generate unique tab ID
  useEffect(() => {
    if (!sessionStorage.getItem("tabId")) {
      sessionStorage.setItem("tabId", Math.random().toString(36).substr(2, 9));
    }
  }, []);

  // ✅ Enable MSAL's built-in cross-tab synchronization
  useEffect(() => {
    try {
      if (typeof instance.enableAccountStorageEvents === "function") {
        instance.enableAccountStorageEvents();
      }

      const all = instance.getAllAccounts?.() || [];
      const active = instance.getActiveAccount?.();
      if (!active && all.length > 0) {
        instance.setActiveAccount(all[0]);
      }
    } catch (e) {
      console.warn("Failed to enable storage events:", e);
    }
  }, [instance]);

  // ✅ Listen to MSAL events
  useEffect(() => {
    const callbackId = instance.addEventCallback?.((event) => {
      try {
        if (
          event.eventType === EventType.LOGIN_SUCCESS &&
          event.payload?.account
        ) {
          console.log("Login success detected");
          instance.setActiveAccount(event.payload.account);
          AppSession.setLoggedIn();
          TabSync.broadcast({ type: "LOGIN" });
          loginInProgressRef.current = false; // ✅ Reset login flag
          try {
            sessionStorage.removeItem("login_attempt_time"); // ✅ Clear Safari timestamp
          } catch (e) {}
          initRef.current = false;
          setIsLoading(true);
        }

        if (event.eventType === EventType.LOGOUT_SUCCESS) {
          console.log("Logout detected");
          AppSession.clearSession();
          TabSync.broadcast({ type: "LOGOUT" });
          logoutInProgressRef.current = false; // ✅ Reset logout flag
          setUser(null);
          if (avatarUrlRef.current) {
            try {
              URL.revokeObjectURL(avatarUrlRef.current);
            } catch (e) {}
            avatarUrlRef.current = null;
          }
        }

        if (event.eventType === EventType.HANDLE_REDIRECT_END) {
          const active = instance.getActiveAccount?.();
          if (!active) {
            const all = instance.getAllAccounts?.() || [];
            if (all.length > 0) {
              instance.setActiveAccount(all[0]);
            }
          }
        }

        if (event.eventType === EventType.ACCOUNT_ADDED) {
          console.log("Account added detected");
          const all = instance.getAllAccounts?.() || [];
          if (all.length > 0) {
            instance.setActiveAccount(all[0]);
            loginInProgressRef.current = false; // ✅ Reset login flag
            try {
              sessionStorage.removeItem("login_attempt_time"); // ✅ Clear Safari timestamp
            } catch (e) {}
            if (AppSession.isLoggedIn()) {
              initRef.current = false;
              setIsLoading(true);
            }
          }
        }

        if (event.eventType === EventType.ACCOUNT_REMOVED) {
          console.log("Account removed detected");
          AppSession.clearSession();
          const all = instance.getAllAccounts?.() || [];
          if (all.length > 0) {
            instance.setActiveAccount(all[0]);
          } else {
            instance.setActiveAccount(null);
            setUser(null);
          }
        }
      } catch (err) {
        console.error("MSAL event error:", err);
      }
    });

    return () => {
      if (callbackId) instance.removeEventCallback?.(callbackId);
    };
  }, [instance]);

  // ✅ Enhanced cross-tab listener
  useEffect(() => {
    const cleanup = TabSync.listen((event) => {
      console.log("Received cross-tab event:", event.type);

      if (event.type === "LOGIN") {
        AppSession.setLoggedIn();
        const accounts = instance.getAllAccounts?.() || [];
        if (accounts.length > 0 && !user) {
          console.log("Cross-tab: Login detected");
          initRef.current = false;
          setIsLoading(true);
        }
      }

      if (event.type === "LOGOUT") {
        console.log("Cross-tab: Logout detected");
        AppSession.clearSession();
        setUser(null);
        if (avatarUrlRef.current) {
          try {
            URL.revokeObjectURL(avatarUrlRef.current);
          } catch (e) {}
          avatarUrlRef.current = null;
        }
      }
    });

    return cleanup;
  }, [instance, user]);

  // ✅ Safari fallback
  useEffect(() => {
    let intervalId;

    const checkAccountState = () => {
      const now = Date.now();
      if (now - lastCheckRef.current < 2000) return;
      lastCheckRef.current = now;

      const currentAccounts = instance.getAllAccounts?.() || [];
      const hasAccount = currentAccounts.length > 0;
      const hasSession = AppSession.isLoggedIn();

      if (user && !hasAccount) {
        console.log("Check: Logout detected");
        AppSession.clearSession();
        setUser(null);
        if (avatarUrlRef.current) {
          try {
            URL.revokeObjectURL(avatarUrlRef.current);
          } catch (e) {}
          avatarUrlRef.current = null;
        }
      }

      if (!user && hasAccount && hasSession && !isLoading) {
        console.log("Check: Valid session detected");
        initRef.current = false;
        setIsLoading(true);
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkAccountState();
      }
    };

    window.addEventListener("focus", checkAccountState);
    window.addEventListener("visibilitychange", handleVisibilityChange);

    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    if (isSafari) {
      intervalId = setInterval(() => {
        if (document.visibilityState === "visible") {
          checkAccountState();
        }
      }, 5000);
    }

    return () => {
      window.removeEventListener("focus", checkAccountState);
      window.removeEventListener("visibilitychange", handleVisibilityChange);
      if (intervalId) clearInterval(intervalId);
    };
  }, [instance, user, isLoading]);

  const fetchGraph = useCallback(async (url, token, options = {}) => {
    const res = await fetch(url, {
      ...options,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(options.headers || {}),
      },
    });
    if (!res.ok) throw new Error(`Graph error ${res.status}`);
    return res;
  }, []);

  // ✅ Initialize user with silent auto-restore
  useEffect(() => {
    const init = async () => {
      if (initRef.current) return;
      initRef.current = true;

      try {
        const hasSession = AppSession.isLoggedIn();

        const active = instance.getActiveAccount?.();
        const all = instance.getAllAccounts?.() || [];
        const account = active || all[0];

        // ✅ If no session but account exists, try silent restore
        if (!hasSession && account) {
          console.log(
            "No session flag but account found - attempting silent restore",
          );
          try {
            // Try to acquire token silently to verify account is still valid
            const tokenResp = await instance.acquireTokenSilent({
              ...loginRequest,
              account,
            });

            if (tokenResp && tokenResp.accessToken) {
              console.log("Silent restore successful - setting session");
              AppSession.setLoggedIn(); // ✅ Auto-restore session
              // Continue with initialization below
            }
          } catch (restoreError) {
            console.log(
              "Silent restore failed - user needs to login explicitly",
            );
            setUser(null);
            setIsLoading(false);
            return;
          }
        }

        // If still no session after restore attempt, require login
        if (!AppSession.isLoggedIn() || !account) {
          console.log("No valid session");
          setUser(null);
          setIsLoading(false);
          return;
        }

        console.log("Valid session found, initializing user");

        const tokenResp = await instance.acquireTokenSilent({
          ...loginRequest,
          account,
        });
        const accessToken = tokenResp.accessToken;

        let profile = null;
        try {
          const profileRes = await fetchGraph(
            graphConfig.graphMeEndpoint,
            accessToken,
          );
          profile = await profileRes.json();
        } catch (err) {
          console.warn("Failed to fetch profile:", err);
        }

        let avatarUrl = null;
        try {
          const photoRes = await fetchGraph(
            graphConfig.graphPhotoEndpoint,
            accessToken,
          );
          const blob = await photoRes.blob();

          if (avatarUrlRef.current) {
            try {
              URL.revokeObjectURL(avatarUrlRef.current);
            } catch (e) {}
          }

          avatarUrl = URL.createObjectURL(blob);
          avatarUrlRef.current = avatarUrl;
        } catch (err) {
          avatarUrl = null;
        }

        const azureUserData = {
          name: account.name || profile?.displayName || "User",
          email:
            account.username ||
            profile?.mail ||
            profile?.userPrincipalName ||
            "",
          id: account.localAccountId,
          tenantId: account.tenantId,
          profile,
          avatarUrl,
        };

        setUser(azureUserData);

        // ✅ Sync with backend (create/update user and load preferences)
        await syncUserWithBackend(azureUserData);
      } catch (error) {
        console.error("Failed to initialize user:", error);
        AppSession.clearSession();
        setUser(null);
        initRef.current = false;
      } finally {
        setIsLoading(false);
      }
    };

    if ((!user || isLoading) && !initRef.current) {
      init();
    }
  }, [accounts, fetchGraph, instance, user, isLoading, syncUserWithBackend]);

  useEffect(() => {
    return () => {
      if (avatarUrlRef.current) {
        try {
          URL.revokeObjectURL(avatarUrlRef.current);
        } catch (e) {}
      }
      loginInProgressRef.current = false; // ✅ Reset on unmount
      logoutInProgressRef.current = false; // ✅ Reset on unmount
    };
  }, []);

  const login = async () => {
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

    // ✅ Check if login is already in progress using ref
    if (loginInProgressRef.current) {
      console.warn(
        "Login already in progress (ref), ignoring duplicate attempt",
      );
      if (isSafari) {
        console.log("Safari: loginInProgressRef is true");
      }
      return;
    }

    // ✅ Safari-specific: Check localStorage for MSAL interaction status
    try {
      const keys = Object.keys(localStorage);
      const hasInteraction = keys.some(
        (key) =>
          key.includes("msal") &&
          key.includes("interaction") &&
          localStorage.getItem(key) === "interaction_in_progress",
      );

      if (hasInteraction) {
        console.warn(
          "Safari: MSAL interaction detected in localStorage, aborting",
        );
        if (isSafari) {
          const interactionKeys = keys.filter(
            (k) => k.includes("msal") && k.includes("interaction"),
          );
          console.log("Safari: Found MSAL interaction keys:", interactionKeys);
        }
        return;
      }
    } catch (e) {
      console.warn("Failed to check localStorage:", e);
    }

    try {
      loginInProgressRef.current = true;

      if (isSafari) {
        console.log("Safari: Setting login attempt time");
      }

      // ✅ Safari-specific: Set a timestamp flag
      try {
        sessionStorage.setItem("login_attempt_time", Date.now().toString());
      } catch (e) {}

      AppSession.setLoggedIn();

      if (isSafari) {
        console.log("Safari: Calling loginRedirect");
      }

      await instance.loginRedirect(loginRequest);
    } catch (error) {
      console.error("Login failed:", error);

      if (isSafari) {
        console.log("Safari: Login error code:", error.errorCode);
        console.log("Safari: Login error message:", error.message);
      }

      // ✅ Only clear session if it's not an interaction_in_progress error
      if (error.errorCode !== "interaction_in_progress") {
        AppSession.clearSession();
        loginInProgressRef.current = false; // Reset on real errors
        try {
          sessionStorage.removeItem("login_attempt_time");
        } catch (e) {}
      } else {
        console.warn(
          "Authentication already in progress, will complete automatically",
        );
      }
    }
  };

  const logout = () => {
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

    // ✅ Check if logout is already in progress
    if (logoutInProgressRef.current) {
      console.warn("Logout already in progress, ignoring duplicate attempt");
      if (isSafari) {
        console.log("Safari: logoutInProgressRef is true");
      }
      return;
    }

    // ✅ Check localStorage for MSAL interaction status before logout
    try {
      const keys = Object.keys(localStorage);
      const hasInteraction = keys.some(
        (key) =>
          key.includes("msal") &&
          key.includes("interaction") &&
          localStorage.getItem(key) === "interaction_in_progress",
      );

      if (hasInteraction) {
        console.warn("Safari: Cannot logout - MSAL interaction in progress");
        if (isSafari) {
          const interactionKeys = keys.filter(
            (k) => k.includes("msal") && k.includes("interaction"),
          );
          console.log(
            "Safari: Found MSAL interaction keys during logout:",
            interactionKeys,
          );
          alert(
            "Please wait for the current authentication process to complete before logging out.",
          );
        }
        return;
      }
    } catch (e) {
      console.warn("Failed to check localStorage for logout:", e);
    }

    try {
      logoutInProgressRef.current = true; // ✅ Set logout flag

      AppSession.clearSession();

      if (avatarUrlRef.current) {
        try {
          URL.revokeObjectURL(avatarUrlRef.current);
        } catch (e) {}
        avatarUrlRef.current = null;
      }

      // Clear login attempt tracking
      try {
        sessionStorage.removeItem("login_attempt_time");
      } catch (e) {}

      loginInProgressRef.current = false;

      if (isSafari) {
        console.log("Safari: Calling logoutRedirect");
      }

      instance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin,
      });
    } catch (error) {
      console.error("Logout failed:", error);
      logoutInProgressRef.current = false; // ✅ Reset on error

      if (isSafari) {
        console.log("Safari: Logout error code:", error.errorCode);
      }

      if (error.errorCode === "interaction_in_progress") {
        console.warn("Logout aborted: interaction in progress");
        alert(
          "Please wait for the current authentication process to complete before logging out.",
        );
      }
    }
  };

  const getAccessToken = async () => {
    try {
      if (!AppSession.isLoggedIn()) {
        console.warn("No valid session, cannot get token");
        return null;
      }

      const active = instance.getActiveAccount?.() || accounts[0];
      if (!active) return null;

      try {
        const response = await instance.acquireTokenSilent({
          ...loginRequest,
          account: active,
        });
        return response.accessToken;
      } catch (silentError) {
        // ✅ Handle interaction_in_progress error
        if (silentError.errorCode === "interaction_in_progress") {
          console.warn("Authentication already in progress");
          return null;
        }

        try {
          const ssoResp = await instance.ssoSilent({
            ...loginRequest,
            loginHint: active.username,
          });
          return ssoResp.accessToken;
        } catch (ssoError) {
          // ✅ Handle interaction_in_progress error
          if (ssoError.errorCode === "interaction_in_progress") {
            console.warn("Authentication already in progress");
            return null;
          }

          AppSession.clearSession();
          await instance.loginRedirect(loginRequest);
          return null;
        }
      }
    } catch (error) {
      console.error("Failed to acquire token:", error);
      return null;
    }
  };

  const refreshUser = useCallback(() => {
    initRef.current = false;
    setIsLoading(true);
  }, []);

  const value = {
    user,
    backendUser,
    preferences,
    isLoading,
    isAuthenticated: !!user,
    login,
    logout,
    getAccessToken,
    refreshUser,
    updatePreferences,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
